import React, { useEffect } from 'react';
import { Script } from 'gatsby';

const RETRY_DELAY_MS = 200;
const MAX_RETRIES = 50;

declare global {
    interface Window {
        __embeddedMessagingInitAttempts?: number;
        __embeddedMessagingInitDone?: boolean;
        initEmbeddedMessaging?: () => void;
        embeddedservice_bootstrap?: {
            settings: {
                language: string;
            };
            init: (oid: string, username: string, url: string, options: { scrt2URL: string }) => void;
        };
    }
}

export function SalesforceChat() {
    useEffect(() => {
        // Initialize the chat when component mounts
        const initializeChat = () => {
            window.__embeddedMessagingInitAttempts = window.__embeddedMessagingInitAttempts || 0;

            window.initEmbeddedMessaging = function () {
                // Don't run twice if already initialized
                if (window.__embeddedMessagingInitDone) return;

                // Retry while bootstrap isn't ready, but stop after MAX_RETRIES
                if (typeof window.embeddedservice_bootstrap === 'undefined') {
                    if ((window.__embeddedMessagingInitAttempts || 0) >= MAX_RETRIES) {
                        console.error(
                            'Embedded Messaging bootstrap never became available. ' +
                                'Giving up after ' +
                                window.__embeddedMessagingInitAttempts +
                                ' attempts (~' +
                                RETRY_DELAY_MS * (window.__embeddedMessagingInitAttempts || 0) +
                                'ms).'
                        );
                        return;
                    }
                    window.__embeddedMessagingInitAttempts = (window.__embeddedMessagingInitAttempts || 0) + 1;
                    setTimeout(window.initEmbeddedMessaging!, RETRY_DELAY_MS);
                    return;
                }

                // Mark as done to prevent racey double-inits; reset on failure
                window.__embeddedMessagingInitDone = true;
                try {
                    window.embeddedservice_bootstrap!.settings.language = 'en_US';
                    window.embeddedservice_bootstrap!.init(
                        process.env.GATSBY_CHATBOT_OID!,
                        process.env.GATSBY_CHATBOT_USERNAME!,
                        process.env.GATSBY_CHATBOT_URL!,
                        { scrt2URL: process.env.GATSBY_CHATBOT_SECRET_URL! }
                    );
                    console.log('Embedded Messaging initialized successfully');
                } catch (err) {
                    console.error('Error loading Embedded Messaging:', err);
                    window.__embeddedMessagingInitDone = false;

                    // Optional: retry on init error too (bounded)
                    if ((window.__embeddedMessagingInitAttempts || 0) < MAX_RETRIES) {
                        window.__embeddedMessagingInitAttempts = (window.__embeddedMessagingInitAttempts || 0) + 1;
                        setTimeout(window.initEmbeddedMessaging!, RETRY_DELAY_MS);
                    }
                }
            };

            // Schedule the first attempt after a delay
            setTimeout(window.initEmbeddedMessaging, 1500);
        };

        // Initialize after DOM is ready
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', initializeChat);
        } else {
            initializeChat();
        }

        // Cleanup
        return () => {
            document.removeEventListener('DOMContentLoaded', initializeChat);
        };
    }, []);

    return (
        <>
            <Script
                id="salesforce-chat-bootstrap"
                src={`${process.env.GATSBY_CHATBOT_URL}/assets/js/bootstrap.min.js`}
                strategy="post-hydrate"
            />
        </>
    );
}
